import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import NightLayout from "../components/night/NightLayout";
import "../styles/night-legal.css";

const updatedAt = "June 27, 2026";

const sections = [
  {
    title: "Scope of this agreement",
    body: [
      "This Distribution Agreement applies when an artist, label, manager, distributor, company, or authorized representative uses BNE Studio to submit music, artwork, metadata, video, lyric, rights, payout, or catalog information for distribution or related administration.",
      "This agreement works together with the BNE Studio Terms of Use, Privacy Policy, product-specific disclosures, store and partner rules, and any separate written agreement signed by the parties. If a signed written agreement conflicts with this page, the signed written agreement controls for that conflict.",
    ],
  },
  {
    title: "Your authority",
    body: [
      "You represent that you have full legal authority to submit each recording, composition, artwork file, contributor credit, artist name, label name, video, lyric, metadata field, split instruction, and payout instruction you provide to BNE Studio.",
      "If you submit content on behalf of another person, artist, group, label, company, estate, or rights holder, you represent that you are authorized to bind that party and administer the submitted content through BNE Studio.",
      "You are responsible for resolving disputes among artists, labels, producers, writers, performers, managers, publishers, heirs, companies, and other contributors or rights holders.",
    ],
  },
  {
    title: "Rights granted to BNE Studio",
    body: [
      "You grant BNE Studio a worldwide, non-exclusive license to reproduce, encode, host, store, transmit, distribute, publicly perform, publicly display, promote, monetize, administer, report on, and otherwise use submitted content and metadata as needed to provide distribution and related services.",
      "This license includes delivery to digital service providers, streaming platforms, download stores, social platforms, content identification services, licensing partners, analytics vendors, payment processors, and operational service providers.",
      "BNE Studio may create technical copies, clips, previews, cover-art derivatives, metadata packages, identifiers, delivery files, accounting records, logs, and reporting artifacts required to operate the service.",
      "The license continues after removal or termination as needed to complete takedowns, collect and pay revenue, process disputes, respond to claims, preserve records, comply with law, and administer prior distributions.",
    ],
  },
  {
    title: "Rights you retain",
    body: [
      "BNE Studio does not take ownership of your recordings, compositions, artwork, artist names, label names, trademarks, or other submitted materials solely because you use distribution services.",
      "Except for rights needed to provide services, you retain the rights you already own or control. You may distribute content through other providers unless a separate written agreement says otherwise.",
    ],
  },
  {
    title: "Delivery, review, and partner acceptance",
    body: [
      "BNE Studio may review submissions for completeness, formatting, rights risk, metadata quality, partner eligibility, fraud indicators, payment status, account status, and compliance with law, BNE Studio policies, and partner requirements.",
      "BNE Studio may reject, delay, modify formatting for, suspend, redeliver, or remove any release or asset that appears inaccurate, incomplete, duplicative, fraudulent, misleading, infringing, abusive, low quality, noncompliant, or risky.",
      "Stores and partners control their own acceptance, display, availability, editorial treatment, search indexing, playlisting, monetization, takedown timing, policies, and technical behavior. BNE Studio cannot guarantee acceptance, placement, availability, timing, earnings, or promotion.",
    ],
  },
  {
    title: "Metadata, identifiers, and catalog records",
    body: [
      "You are responsible for accurate titles, versions, artist names, featured artists, contributors, labels, genres, languages, release dates, territories, explicit-content flags, lyrics, artwork, ISRCs, UPCs, publishing data, split data, and ownership information.",
      "BNE Studio may assign, request, store, validate, display, or transmit identifiers such as UPCs, ISRCs, internal catalog IDs, payment IDs, and partner delivery IDs. Some identifiers may be retained for accounting, compliance, dispute, and reporting purposes.",
      "Incorrect metadata can delay delivery, cause store rejection, create ownership disputes, misdirect payments, or require takedowns and redelivery.",
    ],
  },
  {
    title: "Royalties and accounting",
    body: [
      "BNE Studio will account for royalties and revenue based on amounts actually received or reported as payable to BNE Studio or its applicable partners for your content, subject to deductions, adjustments, reversals, reserves, fees, taxes, payment costs, currency conversion, recoupments, chargebacks, partner penalties, and amounts owed under another agreement.",
      "Reports may be estimates, delayed, corrected, reversed, or adjusted by stores, partners, payment providers, or BNE Studio. A report does not guarantee that money has been received or is immediately payable.",
      "BNE Studio may hold reserves or delay payments when fraud, disputes, missing tax information, missing payment details, abnormal activity, partner review, copyright claims, chargebacks, or compliance issues exist.",
    ],
  },
  {
    title: "Payments, taxes, and withholding",
    body: [
      "You must provide accurate payout, identity, address, banking, tax, and recipient information before payment can be made. BNE Studio may require verification before enabling or continuing payouts.",
      "You are responsible for taxes, tax forms, bank charges, payment provider fees, failed payment costs, currency conversion, and any other charges related to receiving payments.",
      "BNE Studio may withhold, offset, recoup, or deduct amounts as required by law, partner rules, payment processor rules, fraud review, claim resolution, account status, or amounts owed to BNE Studio.",
    ],
  },
  {
    title: "Splits, recoupments, and team payouts",
    body: [
      "If you configure splits, recoupments, royalty shares, team members, collaborators, or payout recipients, you are responsible for the accuracy and authorization of those instructions.",
      "BNE Studio may rely on instructions submitted through the account. BNE Studio is not responsible for private agreements, collaborator disputes, incorrect percentages, missing recipients, unaccepted invitations, expired payment details, or disagreements over ownership.",
      "BNE Studio may pause split changes, payments, takedowns, or catalog updates when a dispute exists or when documentation is insufficient.",
    ],
  },
  {
    title: "Takedowns and post-release changes",
    body: [
      "You may request takedowns, metadata corrections, artwork changes, territory changes, or other catalog updates through available tools or support channels. BNE Studio will process eligible requests through applicable partners, but timing is not guaranteed.",
      "Stores may continue to display, cache, report, reference, or retain content or metadata after a takedown for archival, playlist, reporting, accounting, legal, search, or technical reasons.",
      "BNE Studio may initiate takedowns or restrictions without your request when required by law, partner policy, rights-owner notice, fraud review, account status, nonpayment, or risk management.",
    ],
  },
  {
    title: "Fraud and artificial streaming",
    body: [
      "You may not use bots, click farms, paid artificial streaming, stream manipulation, fake saves, fake follows, misleading ads, unauthorized incentives, abnormal engagement schemes, hacked accounts, or any method intended to deceive stores, charts, fans, partners, or BNE Studio.",
      "If abnormal activity is detected or suspected, BNE Studio may withhold revenue, remove releases, suspend accounts, reverse payments, report activity to partners, and provide relevant information to stores, rights holders, payment processors, or legal authorities.",
      "You are responsible for partner penalties, revenue forfeitures, deductions, claims, damages, and costs connected to artificial activity or other abuse involving your content or account.",
    ],
  },
  {
    title: "Copyright, samples, and third-party rights",
    body: [
      "You are responsible for clearing all recordings, compositions, samples, interpolations, loops, beats, remixes, translations, cover songs, public domain claims, producer contributions, performer contributions, artwork, videos, names, likenesses, trademarks, and other third-party materials.",
      "BNE Studio does not verify that every submission is legally cleared. Submission, approval, delivery, or availability through BNE Studio does not mean BNE Studio has confirmed your rights.",
      "If BNE Studio receives a claim, notice, dispute, or partner inquiry, you must promptly provide proof of rights, licenses, consents, ownership, and authority.",
    ],
  },
  {
    title: "Claims, disputes, and cooperation",
    body: [
      "BNE Studio may remove, restrict, hold revenue for, or suspend content or accounts involved in copyright claims, trademark claims, impersonation claims, ownership disputes, payment disputes, fraud reports, or other legal or partner issues.",
      "You agree to cooperate with BNE Studio, provide requested documentation, and respond promptly to issues involving your catalog, rights, payments, or account.",
      "BNE Studio may share relevant information with stores, payment providers, rights holders, claimants, legal advisors, and authorities when reasonably necessary to resolve claims, enforce rights, prevent abuse, or comply with law.",
    ],
  },
  {
    title: "Content standards and restrictions",
    body: [
      "You may not submit content that is unlawful, infringing, fraudulent, defamatory, exploitative, malicious, deceptive, hateful, harassing, privacy-invasive, or otherwise prohibited by BNE Studio or partner policies.",
      "BNE Studio may restrict content involving deceptive artist names, misleading metadata, unofficial compilations, unauthorized karaoke, soundalikes, generic audio, spam, duplicate releases, public domain misuse, or low-value catalog practices that create partner or rights risk.",
    ],
  },
  {
    title: "Fees and costs",
    body: [
      "Distribution services may include subscriptions, one-time fees, commissions, revenue shares, payment processing fees, partner charges, takedown fees, optional service fees, or other costs disclosed through the product, checkout, order flow, or a separate written agreement.",
      "Failure to pay amounts owed may result in suspended services, withheld payments, account restrictions, or removal of content.",
    ],
  },
  {
    title: "Term, termination, and survival",
    body: [
      "This agreement begins when you use BNE Studio distribution services or submit content for distribution and continues while BNE Studio administers your content, account, reports, payments, claims, takedowns, or related records.",
      "Either party may stop future use of distribution services as allowed by the platform or a separate agreement. BNE Studio may suspend or terminate distribution services for breach, nonpayment, rights risk, fraud risk, partner requirement, legal compliance, or platform abuse.",
      "Rights and obligations related to accounting, payments, disputes, indemnity, limitations of liability, record retention, takedowns, compliance, and prior distributions survive termination.",
    ],
  },
  {
    title: "Indemnity and liability",
    body: [
      "You agree to defend, indemnify, and hold harmless BNE Studio and its affiliates, partners, service providers, officers, employees, contractors, and agents from claims, losses, liabilities, damages, penalties, costs, and expenses, including reasonable attorneys' fees, arising from your content, rights, metadata, account activity, payment instructions, fraud, disputes, or breach of this agreement.",
      "To the fullest extent permitted by law, BNE Studio is not liable for indirect, incidental, special, consequential, exemplary, or punitive damages, lost profits, lost revenue, lost data, reputational harm, partner decisions, store delays, or commercial outcomes.",
    ],
  },
];

export default function DistributionAgreementPage() {
  useEffect(() => {
    const prev = document.title;
    document.title = "Distribution Agreement - BNE Studio";
    return () => {
      document.title = prev;
    };
  }, []);

  return (
    <NightLayout>
      <main className="legal-page">
        <header className="legal-hero">
          <div className="wrap">
            <span className="eyebrow dot">Distribution</span>
            <h1 className="grad-text">Distribution Agreement</h1>
            <p>
              This agreement is for artists, labels, companies, and authorized
              representatives using BNE Studio to distribute music and administer
              related catalog, rights, reporting, and payout workflows.
            </p>
            <div className="legal-meta">
              <span>Last updated: {updatedAt}</span>
              <Link to="/terms">Terms of Use</Link>
              <Link to="/contact-us">Questions</Link>
            </div>
          </div>
        </header>

        <section className="legal-section">
          <div className="wrap legal-grid legal-grid-single">
            <div className="legal-stack">
              {sections.map((section) => (
                <article className="legal-card" key={section.title}>
                  <h3>{section.title}</h3>
                  {section.body.map((paragraph) => (
                    <p key={paragraph}>{paragraph}</p>
                  ))}
                </article>
              ))}

              <article className="legal-card legal-callout">
                <h3>Need help with a release?</h3>
                <p>
                  Contact BNE Studio before submitting if you are unsure about
                  rights, ownership, credits, metadata, splits, or payout
                  authority.
                </p>
                <Link className="btn btn-primary" to="/contact-us">
                  Contact us
                </Link>
              </article>
            </div>
          </div>
        </section>
      </main>
    </NightLayout>
  );
}
